import React, {
  useState,
  useRef,
  useImperativeHandle,
  useEffect,
  useLayoutEffect,
} from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import { ActionOption } from 'components/ActionMenu';
import { ButtonV3 } from 'components/ComponentV2';
import styles from './CustomTextInput.module.scss';

interface CustomInputProps {
  options: ActionOption[];
  textAreaClassName?: string;
  wrapperClassName?: string;
  updateContent: (content: string) => void;
  placeholder?: string;
  setIsCursorInside: (val: boolean) => void;
  isDisabled?: boolean;
}
type CustomInputRef = {
  insertSelect: (defaultValue: string, id: string, code: string) => void;
};
interface CustomButtonProps {
  defaultValue: string;
  value?: string;
  onRemove: () => void;
}

const CustomButton: React.FC<CustomButtonProps> = ({
  defaultValue,
  value,
  onRemove,
}) => {
  const [code, setCode] = useState('');
  const [selectedValue, setSelectedValue] = useState('');

  useLayoutEffect(() => {
    setCode(value || '');
  }, [value]);

  useEffect(() => {
    setSelectedValue(defaultValue);
  }, [defaultValue]);

  return (
    <ButtonV3
      size="small"
      type="submit"
      onClick={onRemove}
      endIcon="close"
      tabIndex={-1}
      className={styles.variableButton}
      color="secondary"
      data-code={code}
      data-id="variable-button"
    >
      {selectedValue}
    </ButtonV3>
  );
};

const CustomInput = React.forwardRef<CustomInputRef, CustomInputProps>(
  (props, ref) => {
    const {
      options,
      textAreaClassName,
      wrapperClassName,
      updateContent,
      placeholder = '',
      setIsCursorInside,
      isDisabled = false,
    } = props;
    const [content, setContent] = useState<string>('');

    const editableRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      insertSelect: (defaultValue: string) => {
        const code =
          options.find((opt) => opt.label === defaultValue)?.id || '';
        insertSelectAtCaret(defaultValue, code);
        handleUpdateContent();
      },
      addContent: (content: string) => {
        if (editableRef.current) {
          const formattedContent = content.replace(/\n/g, '<br>');
          editableRef.current.innerHTML = formattedContent;
        }
      },
    }));

    const isEmptyContent = (htmlContent: string) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      return !tempDiv.textContent?.trim();
    };

    useEffect(() => {
      handleUpdateContent();
    }, [content]);

    useEffect(() => {
      if (editableRef.current) {
        const isEmpty = isEmptyContent(content);
        editableRef.current.setAttribute(
          'data-placeholder',
          isEmpty ? placeholder : ''
        );
      }
    }, [content, placeholder]);

    useEffect(() => {
      // Function to call when clicking anywhere in the document
      const handleDocumentClick = (event: MouseEvent) => {
        // Check if the click happened inside the element with ID 'parentElement'
        const isInsideCaret = isCaretInEditableDiv();
        if (
          !(event.target as HTMLElement).closest('#modal-actions') ||
          !isInsideCaret
        ) {
          setIsCursorInside(false);
        }
      };

      // Attach the listener to the document
      document.addEventListener('click', handleDocumentClick, true);

      // Cleanup
      return () => {
        document.removeEventListener('click', handleDocumentClick, true);
      };
    }, []);

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation(); // Prevent the document click handler from firing
      setIsCursorInside(true);
    };

    const insertSelectAtCaret = (value: string, code: string): void => {
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;

      const range = sel.getRangeAt(0).cloneRange();
      range.deleteContents();

      // Insert a text node (a non-breaking space) to maintain formatting
      const space = document.createTextNode(' '); // Non-breaking space
      range.insertNode(space);

      // Now insert the placeholder for the button
      const placeholder = document.createElement('span');
      placeholder.className = 'select-placeholder';
      placeholder.contentEditable = 'false';
      range.insertNode(placeholder);

      // Render the React component inside the placeholder
      ReactDOM.render(
        <CustomButton
          defaultValue={value}
          value={code}
          onRemove={() => {
            removeButton(placeholder);
            setIsCursorInside(true);
          }}
        />,
        placeholder
      );

      // Update the range to point after the placeholder and space
      range.setStartAfter(placeholder);

      // Insert another non-breaking space after the placeholder
      // to ensure there is space after the button where the user can click to place the cursor.
      const spaceAfter = document.createTextNode(' ');
      range.insertNode(spaceAfter);

      // Move the caret to after the second non-breaking space
      range.setStartAfter(spaceAfter);
      range.collapse(true);

      // Update selection
      sel.removeAllRanges();
      sel.addRange(range);

      // Update state to reflect changes in the editable content
      if (editableRef.current) {
        setContent(editableRef.current.innerHTML);
      }
    };

    const removeButton = (buttonElement: HTMLElement) => {
      if (buttonElement) {
        const parent = buttonElement.parentNode;
        if (parent) {
          // Remove the button element
          parent.removeChild(buttonElement);

          // Update state and position the cursor correctly
          if (editableRef.current) {
            setContent(editableRef.current.innerHTML);
            positionCaretAfterElement(parent);
            editableRef.current.focus();
          }
        }
      }
    };

    const positionCaretAfterElement = (element: Node) => {
      const range = document.createRange();
      const sel = window.getSelection();

      range.setStartAfter(element);
      range.collapse(true);

      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    };

    function extractSimpleText(htmlString: string): string {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');

      // Process <button> elements
      const buttons = Array.from(doc.querySelectorAll('button[data-code]'));
      buttons.forEach((button) => {
        const specialAttr =
          ' ' + (button.getAttribute('data-code') || '') + ' ';
        const textNode = doc.createTextNode(specialAttr);
        button.parentNode?.replaceChild(textNode, button);
      });

      // Handle add new line after each node
      Array.from(doc.body.childNodes).forEach((element) => {
        // Add a newline at the end of element
        element?.after('\n');

        if (
          element instanceof HTMLElement &&
          Array.from(element.children).length > 1
        ) {
          Array.from(element?.children).forEach((nestedElements) => {
            // Convert the element itself into a text node with its content
            const textNode = doc.createTextNode(
              nestedElements.textContent || ''
            );
            nestedElements?.parentNode?.replaceChild(textNode, nestedElements);

            // Add a newline at the end if it's not the last child of its parent
            if (nestedElements.nextSibling) {
              nestedElements?.parentNode?.insertBefore(
                doc.createTextNode('\n'),
                nestedElements.nextSibling
              );
            }
          });
        } else {
          // Convert the element itself into a text node with its content
          const textNode = document.createTextNode(element.textContent || '');
          element?.parentNode?.replaceChild(textNode, element);
        }
      });

      // Retrieve and clean text content from the body
      const textContent = doc.body.textContent || '';

      return textContent.trim();
    }

    function isCaretInEditableDiv(): boolean {
      const selection = window.getSelection();

      if (!selection || selection.rangeCount === 0) {
        return false;
      }

      const range = selection.getRangeAt(0);
      let container: Node | null = range.commonAncestorContainer;

      // Traverse up the DOM tree and check if any parent is a contenteditable div
      while (container) {
        // Ensuring the container is an Element before accessing Element-specific properties
        if (container.nodeType === Node.ELEMENT_NODE) {
          const element = container as Element;
          if (element.getAttribute('contenteditable') === 'true') {
            return true;
          }
        }
        container = container.parentNode;
      }

      return false;
    }

    const handleUpdateContent = () => {
      if (!editableRef?.current) return;
      updateContent(extractSimpleText(editableRef.current.innerHTML));
    };

    return (
      <div className={wrapperClassName}>
        <div
          ref={editableRef}
          contentEditable={isDisabled ? false : true}
          onInput={() => {
            if (editableRef.current) {
              // to remove styles from all elements when pasting
              const nestedElements = editableRef.current.querySelectorAll('*');
              // Loop through each nested element and remove inline styles
              nestedElements.forEach((element) => {
                element.removeAttribute('style');
              });
              setContent(editableRef.current.innerHTML);
            }
          }}
          className={cn(styles.CustomTextInput, textAreaClassName)}
          data-content="true"
          data-placeholder={placeholder} // Initial placeholder value
          onClick={handleClick} // Attaching the click event listener
        ></div>
      </div>
    );
  }
);

CustomInput.displayName = 'CustomInput';

export default CustomInput;
